import { computed, ref } from 'vue';
import ConversationTransferRequestsAPI from 'dashboard/api/aceleres/conversationTransferRequests';
import AssignmentTransferRequestsAPI from 'dashboard/api/aceleres/assignmentTransferRequests';
import ContactOwnershipRequestsAPI from 'dashboard/api/aceleres/contactOwnershipRequests';
import FollowupNotificationsAPI from 'dashboard/api/aceleres/followupNotifications';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

const requestsUnreadCount = ref(0);
const reminders = ref([]);
const remindersLoading = ref(false);
const remindersError = ref(false);
const remindersUnreadCount = computed(
  () =>
    reminders.value.filter(item => item.has_notification && !item.read_at)
      .length
);
const notificationsUnreadCount = computed(
  () => requestsUnreadCount.value + remindersUnreadCount.value
);

let isSyncStarted = false;
let syncSequence = 0;
let remindersSequence = 0;
let remindersRequest = null;
let syncedAccountId = null;
let unreadCountRefreshTimer = null;

const currentAccountId = () => {
  const match = window.location.pathname.match(/\/app\/accounts\/(\d+)/);
  return match?.[1] || null;
};

const countUnreadRequests = async () => {
  const [portfolio, attendance, ownership] = await Promise.all([
    ConversationTransferRequestsAPI.team(),
    AssignmentTransferRequestsAPI.team(),
    ContactOwnershipRequestsAPI.team(),
  ]);
  const requests = [
    ...(portfolio?.data?.payload || []),
    ...(attendance?.data?.payload || []),
    ...(ownership?.data?.payload || []),
  ];
  const uniqueRequests = new Map(
    requests.map(request => [`${request.kind}-${request.id}`, request])
  );

  return [...uniqueRequests.values()].filter(
    request => request.notification_id && !request.notification_read_at
  ).length;
};

const refreshUnreadCounts = async () => {
  const accountId = currentAccountId();
  if (!accountId) return;

  if (accountId !== syncedAccountId) {
    syncedAccountId = accountId;
    requestsUnreadCount.value = 0;
    reminders.value = [];
    remindersSequence += 1;
    remindersRequest = null;
  }

  syncSequence += 1;
  const sequence = syncSequence;
  const [requestsResult] = await Promise.allSettled([
    countUnreadRequests(),
    refreshReminders(),
  ]);

  if (sequence !== syncSequence || accountId !== currentAccountId()) return;

  if (requestsResult.status === 'fulfilled') {
    requestsUnreadCount.value = requestsResult.value;
  }
};

const refreshOnVisibility = () => {
  if (document.visibilityState === 'visible') refreshUnreadCounts();
};

const scheduleUnreadCountRefresh = () => {
  window.clearTimeout(unreadCountRefreshTimer);
  unreadCountRefreshTimer = window.setTimeout(refreshUnreadCounts, 250);
};

const startUnreadCountSync = () => {
  if (isSyncStarted) return;
  isSyncStarted = true;

  emitter.on(
    BUS_EVENTS.ACELERES_CARTEIRIZACAO_REQUEST_UPDATED,
    refreshUnreadCounts
  );
  emitter.on(
    BUS_EVENTS.ACELERES_ATENDIMENTO_REQUEST_UPDATED,
    refreshUnreadCounts
  );
  emitter.on(BUS_EVENTS.ACELERES_REMINDERS_UPDATED, () =>
    refreshReminders({ force: true })
  );
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, refreshUnreadCounts);
  emitter.on(BUS_EVENTS.INBOX_NOTIFICATION_UPDATED, scheduleUnreadCountRefresh);
  window.addEventListener('focus', refreshUnreadCounts);
  document.addEventListener('visibilitychange', refreshOnVisibility);
  window.setInterval(refreshOnVisibility, 60000);
  refreshUnreadCounts();
};

const setRequestsUnreadCount = count => {
  syncSequence += 1;
  requestsUnreadCount.value = Math.max(0, Number(count) || 0);
};

const refreshReminders = ({ force = false } = {}) => {
  if (remindersRequest && !force) return remindersRequest;
  const accountId = currentAccountId();
  const sequence = ++remindersSequence;
  remindersLoading.value = true;
  remindersError.value = false;
  remindersRequest = FollowupNotificationsAPI.index()
    .then(result => {
      if (sequence !== remindersSequence || accountId !== currentAccountId())
        return;
      reminders.value = (result.items || []).filter(
        item => !item.notification_dismissed
      );
    })
    .catch(() => {
      if (sequence === remindersSequence) remindersError.value = true;
    })
    .finally(() => {
      if (sequence !== remindersSequence) return;
      remindersLoading.value = false;
      remindersRequest = null;
    });
  return remindersRequest;
};

const setReminders = items => {
  // Uma resposta iniciada antes da ação não pode desfazer a leitura ou exclusão.
  remindersSequence += 1;
  remindersRequest = null;
  remindersLoading.value = false;
  reminders.value = items;
};

export const useInboxUnreadCounts = () => ({
  requestsUnreadCount,
  remindersUnreadCount,
  notificationsUnreadCount,
  refreshUnreadCounts,
  setRequestsUnreadCount,
  reminders,
  remindersLoading,
  remindersError,
  refreshReminders,
  setReminders,
  startUnreadCountSync,
});
